import i18nConfig from '@/config/i18n'
import { useRuntimeConfig } from 'nuxt/app'
import { defineStore } from 'pinia'
import type { CountryCodes, Locales } from '@/enums/general'

const { countries, locales, defaultCountry, defaultLocale } = i18nConfig

interface BackButtonState {
  visible: boolean
  text: string | null
  to: string | null
}

interface AppState {
  countries: (typeof CountryCodes)[keyof typeof CountryCodes][]
  countriesData: Record<string, any>
  country: (typeof CountryCodes)[keyof typeof CountryCodes]
  defaultCountry: (typeof CountryCodes)[keyof typeof CountryCodes]
  locales: (typeof Locales)[keyof typeof Locales][]
  locale: (typeof Locales)[keyof typeof Locales]
  backButton: BackButtonState
}

export const useAppStore = defineStore('app', {
  state: (): AppState => ({
    countries: countries as (typeof CountryCodes)[keyof typeof CountryCodes][],
    countriesData: {},
    country: defaultCountry as (typeof CountryCodes)[keyof typeof CountryCodes],
    defaultCountry: defaultCountry as (typeof CountryCodes)[keyof typeof CountryCodes],
    locales: locales as (typeof Locales)[keyof typeof Locales][],
    locale: defaultLocale as (typeof Locales)[keyof typeof Locales],
    backButton: {
      visible: false,
      text: null,
      to: null
    }
  }),
  getters: {
    backButtonData: (state: AppState) => state.backButton,
    countryData: (state: AppState) => state.countriesData[state.country]
  },
  actions: {
    setCountry(country: (typeof CountryCodes)[keyof typeof CountryCodes]) {
      if (this.countries.includes(country)) {
        this.country = country
      }
    },
    setLanguage(locale: (typeof Locales)[keyof typeof Locales]) {
      if (this.locales.includes(locale)) {
        this.locale = locale
      }
    },
    setBackButton({ visible, text, to }: { visible: boolean; text?: string; to?: string }) {
      this.backButton.visible = visible
      this.backButton.to = to || null
      this.backButton.text = text || null
    },
    setCountries(countriesData: Record<string, any>) {
      this.countriesData = countriesData
    },
    async getCountry({ country }: { country: string }) {
      const {
        public: { API_URL }
      } = useRuntimeConfig()
      return await $fetch<any>(`${API_URL}/v2/countries/${country}`)
    },
    async fetchCountries() {
      const {
        public: { API_URL }
      } = useRuntimeConfig()
      const data = await $fetch<any[]>(`${API_URL}/v2/countries`)

      const countriesData = data.reduce((acc: Record<string, any>, country: any) => {
        acc[country.code.toLowerCase()] = country
        return acc
      }, {})

      this.setCountries(countriesData)

      return data
    }
  }
})
